import { useState, useEffect } from 'react';
import {
    Container, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Helmet from '../components/Helmet/Helmet';
import ProductAccordion from '../components/Accordion/ProductAccordion';
import { useDispatch, useSelector } from 'react-redux';
import { cartActions } from '../redux/slices/cartSlice';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { toast } from 'react-toastify';
import '../styles/product-details.css';
import Carousel from '../components/Carousel/Carousel';
import ImageLightboxViewer from '../components/ImageLightboxViewer';

const ProductDetails = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [carouselImages, setCarouselImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);
    const [showSizeGuide, setShowSizeGuide] = useState(false);
    const [showInches, setShowInches] = useState(true);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const cartItems = useSelector(state => state.cart?.items ?? []);

    const lastActionStatus = useSelector(state => state.cart.lastActionStatus);

    useEffect(() => {
        if (!lastActionStatus) return;

        const { success, message, product } = lastActionStatus;

        if (success) {
            toast(<CustomToast product={product} message={message} />, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                closeButton: false,
                className: "add-to-cart-toast",
            });
        } else {
            toast.error(message);
        }

        dispatch(cartActions.clearActionStatus());
    }, [lastActionStatus]);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const docRef = doc(db, "products", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const prod = { id: docSnap.id, ...docSnap.data() };
                    setProduct(prod);
                    const defaultVariation = prod.variations?.[0];

                    if (defaultVariation) {
                        setSelectedVariation(defaultVariation);
                        setSelectedSize(defaultVariation.sizes?.[0] || null);
                        const variationImages = extractImageUrls(defaultVariation.imgUrl);
                        const fallbackImages = extractImageUrls(prod.imgUrl);
                        setCarouselImages(variationImages.length > 0 ? variationImages : fallbackImages);
                    }                    
                }
            } catch (err) {
                console.error("Error loading product:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);

    

    const toggle = () => setDropdownOpen(prev => !prev);

    const extractImageUrls = (imgSource) => {
        if (Array.isArray(imgSource)) return imgSource;
        if (typeof imgSource === 'string' && imgSource.trim() !== '') return [imgSource];
        return [];
    };

    const handleColorSwitch = (variation) => {
        setSelectedVariation(variation);
        setSelectedSize(variation.sizes?.[0] || null);
    
        const variationImages = extractImageUrls(variation.imgUrl);
        setCarouselImages(variationImages);
    };
    

    const handleQuantityChange = (e) => {
        let newQuantity = e.target.valueAsNumber;
        if (newQuantity < 1) {
            setQuantity(0);
        }

        setQuantity(e.target.valueAsNumber);
    };

    const isOutOfStock = () => {
        return !selectedSize || selectedSize.quantity <= 0;
    };

    const CustomToast = ({ product, message }) => (
        <div className="cart_custom-toast">
            <span className="cart_toast-icon"><i className="ri-checkbox-circle-line"></i></span>
            <div className="cart_toast-message">
                <p className="cart_toast-text">{message}</p>
            </div>
            <img src={product.imgUrl} loading='lazy' alt={product.variationName || product.productName} className="cart_toast-image" />
            <span 
                className="cart_toast-close" 
                onClick={() => toast.dismiss()}
                role="button" 
                tabIndex={0} 
                aria-label="Close toast"
                style={{ cursor: "pointer" }}
            >
                <i class="ri-close-circle-fill"></i>
            </span>
        </div>
    );

    const addToCart = () => {
        if (!selectedVariation || !selectedSize) return;

        const isDefaultVariation = selectedVariation.variationName === "";
        const productName = product.productName;
        const variationName = selectedVariation.variationName?.toLowerCase() || '';

        const itemId = `${product.id}-${product.productName}-${variationName || ''}`;

        const itemToAdd = {
            itemId,
            imgUrl: Array.isArray(selectedVariation.imgUrl)
            ? selectedVariation.imgUrl[0]
            : selectedVariation.imgUrl || (Array.isArray(product.imgUrl) ? product.imgUrl[0] : product.imgUrl),
            vidUrl: product.vidUrl,
            category: product.category,
            productSize: product.productSize,
            weight: product.weight,
            productName,
            price: selectedSize.price || selectedVariation.price || product.price,
            details: selectedVariation.details || product.details,
            selectedSize: selectedSize.size,
            selectedColor: isDefaultVariation ? null : selectedVariation.color,
            variationName,
            quantity,
            stockQty: selectedSize.quantity, // ✅ important for reducer validation
        };

        dispatch(cartActions.addItem(itemToAdd));
    };

    const handleBuyNow = () => {
        if (!selectedVariation || !selectedSize) return;
    
        if (selectedSize.quantity <= 0) {
            toast.error("Selected size is out of stock");
            return;
        }
    
        const isDefaultVariation = selectedVariation.variationName === "";
        const variationName = selectedVariation.variationName?.toLowerCase() || '';
        const itemId = `${product.id}-${product.productName}-${variationName}`;
    
        const itemToPurchase = {
            itemId,
            imgUrl: selectedVariation.imgUrl || product.imgUrl[0],
            vidUrl: product.vidUrl,
            category: product.category,
            productSize: product.productSize,
            weight: product.weight,
            productName: product.productName,
            price: selectedSize.price || selectedVariation.price || product.price,
            details: selectedVariation.details || product.details,
            selectedSize: selectedSize.size,
            selectedColor: isDefaultVariation ? null : selectedVariation.color,
            variationName,
            quantity,
        };
    
        dispatch(cartActions.setDirectPurchaseItem(itemToPurchase));
        navigate('/checkout');
    };



    if (loading) return <div>Loading...</div>;
    if (!product) return <div>Product not found</div>;

    const isNFT = product.category === 'NFT';
    const isTshirt = product.productCategory === 'tshirts';
    const accordionData = [
        { title: 'Details', content: product.details },
        ...(isNFT ? [] : [
            ...(isTshirt
                ? [
                    {
                        title: 'Sizing Guide',
                        content: (
                        <span>
                            Consult our size{" "}
                            <span
                            onClick={() => setShowSizeGuide(true)}
                            style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                            >
                            guide
                            </span>
                        </span>
                        ),
                    },
                    ]
                : []),
            { title: 'Shipping', content: product.shipping },
            { title: 'Returns', content: product.returns },
        ])
    ];

    return (
        <Helmet title="Product details">
            <section className="product-section">
                <Container>
                    <Row>
                        <Col lg="6">
                            <div className="product-image-container">
                                {isNFT ? (
                                    <video
                                        src={product.vidUrl}
                                        autoPlay
                                        playsInline
                                        loop
                                        muted
                                        className="responsive-video"
                                    />
                                ) : (
                                    <div className="product-image-container">
                                        {console.log("carouselImages (inline):", carouselImages)}
                                        {carouselImages.length > 0 && (
                                            <>
                                                <Carousel
                                                    imageUrls={carouselImages}
                                                    onImageClick={(idx) => {
                                                        console.log("Clicked image index:", idx);
                                                        setIndex(idx);
                                                        setOpen(true);
                                                        console.log("Lightbox open set to true");
                                                    }}
                                                />

                                                <ImageLightboxViewer 
                                                    open={open} 
                                                    index={index} 
                                                    onOpen={() => {
                                                    console.log("Opening lightbox!");
                                                    setOpen(true);
                                                }}
                                                close={() => {
                                                    console.log("Closing lightbox!");
                                                    setOpen(false);
                                                }}
                                                    slides={carouselImages.map(img => ({ src: img }))}
                                                    showPreview={true}
                                                    // className='details_product_image' 
                                                />
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col lg="6">
                            <div className="product-details">
                                <h1 className="product-name mb-2">{selectedVariation?.variationName || product.productName}</h1>
                                <span className="product-price mb-2">{isNFT ? `${product.price} ETH` : `$${selectedSize?.price || selectedVariation?.price || product.price} USD `}</span>
                                <p className="description mb-2">{product.details}</p>

                                {!isNFT && (
                                    <>
                                        {/* Variation Color Swatches */}
                                        <div className="color-options d-flex gap-2 flex-wrap">
                                            {product.variations?.map((v, idx) => {
                                                if (selectedVariation.variationName !== '') {
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleColorSwitch(v)}
                                                            className={`color-swatch ${selectedVariation?.variationName === v.variationName ? 'active' : ''}`}
                                                            style={{
                                                                backgroundImage: `url(${v.imgUrl})`,
                                                                backgroundSize: 'contain',
                                                                backgroundRepeat: 'no-repeat',
                                                                width: '80px',
                                                                height: '80px',
                                                                // borderRadius: '50%',
                                                                backgroundColor: 'transparent',
                                                                border: selectedVariation?.variationName === v.variationName ? '0px solid #000' : '0px solid #ccc'
                                                            }}
                                                            title={v.variationName}
                                                        />
                                                    )
                                                }
                                            })}
                                        </div>

                                        {/* Quantity and Size */}
                                        <div className="d-flex flex-row align-items-end pb-2">
                                            <div className="quantity">
                                                <span>Quantity</span>
                                                <input
                                                    type="number"
                                                    value={quantity}
                                                    onChange={handleQuantityChange}
                                                    min={1}
                                                    className="quantity-input input-height"
                                                />
                                            </div>

                                            {selectedVariation?.sizes[0]?.size !== '' && (
                                                <div className="size-dropdown">
                                                    <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                                                        <DropdownToggle className="dropdown-toggle input-height" caret>
                                                            {selectedSize?.size || 'Select size'}
                                                        </DropdownToggle>
                                                        <DropdownMenu>
                                                            {selectedVariation.sizes.map((s, idx) => {
                                                                const isDisabled = s.quantity <= 0;
                                                                return (
                                                                    <DropdownItem
                                                                        key={idx}
                                                                        onClick={() => {
                                                                            if (!isDisabled) {
                                                                                setSelectedSize(s);
                                                                            }
                                                                        }}
                                                                        disabled={isDisabled}
                                                                        style={{
                                                                            opacity: isDisabled ? 0.5 : 1,
                                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center'
                                                                        }}
                                                                        title={isDisabled ? 'Out of stock' : `In stock: ${s.quantity}`}
                                                                    >
                                                                        <span>{s.size}</span>
                                                                        {isDisabled && (
                                                                            <small className="text-muted ms-2">(Out of stock)</small>
                                                                        )}
                                                                    </DropdownItem>
                                                                );
                                                            })}
                                                        </DropdownMenu>
                                                    </Dropdown>
                                                </div>
                                            )}
                                        </div>

                                        <button className="product-btn" onClick={addToCart} disabled={isOutOfStock()}>
                                            {isOutOfStock() ? 'Out of Stock' : 'Add to Bag'}
                                        </button>
                                        {!isOutOfStock() && <button className="product-btn" onClick={handleBuyNow}>Buy now</button>}
                                    </>
                                )}

                                <ProductAccordion accordionData={accordionData} />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            {showSizeGuide && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        zIndex: 2000,
                    }}
                >

                    {/* Image */}
                    <img
                        src={
                            showInches
                                ? require("../assets/images/INCHESSIZEGUIDE.png")
                                : require("../assets/images/CMSIZEGUIDE.png")
                        }
                        alt="Size Guide"
                        style={{
                            maxWidth: "90%",
                            maxHeight: "90%",
                            objectFit: "contain",
                            paddingLeft: "5%"
                        }}
                    />

                    {/* Close Button */}
                    <button
                        onClick={() => setShowSizeGuide(false)}
                        style={{
                            position: "absolute",
                            top: 20,
                            right: 20,
                            background: "transparent",
                            color: "black",
                            fontSize: "2rem",
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        ✕
                    </button>

                    {/* Toggle Button */}
                    <div style={{ marginTop: "1rem", display: "flex", gap: "10px" }}>
                        <button
                            onClick={() => setShowInches(true)}
                            className='size_btn'
                            style={{
                                background: showInches ? "black" : "gray",
                                color: showInches ? "white" : "white",
                            }}
                        >
                            Inches
                        </button>
                        <button
                            onClick={() => setShowInches(false)}
                            className='size_btn'
                            style={{
                                background: !showInches ? "black" : "gray",
                                color: !showInches ? "white" : "white",
                            }}
                        >
                            CM
                        </button>
                    </div>
                </div>
            )}
        </Helmet>
    );
};

export default ProductDetails;
